import { useRef, useContext, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import MapContext from "../../../MapContext";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector";
import { setResultsForFeature, setResultsForLayer } from "../../../../redux/resultsSlice";
import { Style, Stroke, Circle as CircleStyle } from "ol/style";
import { Point } from "ol/geom.js";
import Feature from "ol/Feature.js";
import LayerPopup from "../LayerPopup";
import GroupPopup from "../GroupPopup";
import { Button } from "@progress/kendo-react-buttons";

const GeocodeFeatures = () => {
    const vectorHoverLayer = useRef();
    const { map } = useContext(MapContext);
    const vectorLayer = useRef();
    const geocodingData = useSelector((state) => state.resultsData.geocodingData);
    const [geoCodeResults] = useSelector((state) => state.resultsData.resultsForLayer);
    const results = useSelector((state) => state.resultsData.results);

    const dispatch = useDispatch();

    // Add/remove feature to hover layer when hovered over
    const handleMouseHover = (id) => {
        const feature = features.find((f) => f.values_.Id === id);
        vectorHoverLayer.current.getSource().addFeature(feature);
    };
    const handleMouseOut = () => {
        vectorHoverLayer.current.getSource().clear();
    };

    const showFeatureDescription = (feature) => {
        dispatch(setResultsForFeature([{ ...geoCodeResults, Features: [feature] }]));
        vectorLayer.current.getSource().clear();
    };

    const features = useMemo(() => {
        // make sure results match (race condition)
        if (
            geocodingData.candidates[0].attributes.Match_addr !==
            geoCodeResults.Features[0]
        )
            return;
        const features = [];
        // Match feature to geocode results in state
        geoCodeResults.Features.forEach((feat) => {
            const result = geocodingData.candidates.find(
                (c) => c.attributes.Match_addr === feat
            );
            // return if no result found
            if (!result) {
                return;
            }
            // create feature to add to map
            const feature = new Feature({
                geometry: new Point([result.location.x, result.location.y]),
                Id: result.attributes.Match_addr,
                attributes: result.attributes,
            });
            feature.setId(result.attributes.Match_addr);
            features.push(feature);
        });
        return features;
    }, [geoCodeResults, geocodingData]);

    useEffect(() => {
        if (features === undefined) return;
        vectorLayer.current = new VectorLayer({
            source: new VectorSource({ features: features }),
            map: map,
            style: selectedStyle,
            zIndex: 100,
        });
        vectorHoverLayer.current = new VectorLayer({
            source: new VectorSource(),
            map: map,
            style: highlightStyle,
            zIndex: 101,
        });
        return () => {
            vectorLayer.current.getSource().clear();
            vectorHoverLayer.current.getSource().clear();
        };
    }, [features]);

    return (
        <>
            {" "}
            {features && (
                <>
                    {results.length > 1 && (
                        <Button
                            style={{
                                position: "absolute",
                                zIndex: "20",
                                cursor: "pointer",
                            }}
                            icon="arrow-left"
                            onClick={() => {
                                dispatch(setResultsForLayer([]));
                                dispatch(setResultsForFeature([]));
                            }}></Button>
                    )}
                    <div className="results-title">
                        {geoCodeResults.DisplayName} ({geoCodeResults.Features.length})
                        <div style={{ float: "right", marginBottom: "10px" }}>
                            <GroupPopup results={[geoCodeResults]} icon={"menu"} />
                        </div>
                    </div>
                    <ul style={{ listStyleType: "none", padding: 0 }}>
                        {features.map((res) => {
                            return (
                                <div
                                    key={res.ol_uid}
                                    className="results-feature"
                                    onMouseOver={() => handleMouseHover(res.id_)}
                                    onMouseOut={() => handleMouseOut()}>
                                    <ul style={{ listStyleType: "none", padding: 0 }}>
                                        <li className="feature-row">
                                            <div
                                                className="feature-label"
                                                onClick={() =>
                                                    showFeatureDescription(res.id_)
                                                }>
                                                {res.values_.Id}
                                            </div>
                                            <LayerPopup
                                                feature={res}
                                                layerId={"ArcGIS Geocoder"}
                                            />
                                        </li>
                                    </ul>
                                </div>
                            );
                        })}
                    </ul>
                </>
            )}
        </>
    );
};
export default GeocodeFeatures;

const selectedStyle = new Style({
    zIndex: 100,
    stroke: new Stroke({
        color: "red",
        width: 2,
    }),
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "red",
            width: 4,
        }),
    }),
});

const highlightStyle = new Style({
    zIndex: 100,
    stroke: new Stroke({
        color: "blue",
        width: 2,
    }),
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "blue",
            width: 4,
        }),
    }),
});
